import { forwardRef, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import type { DefaultComponentsProvider } from "~/components/content/content-item-render";
import { checkIsMainTopic } from "~/components/content/helpers";
import { sanitizeReadonlyArray } from "~/lib/utils";
import type { magazinesStyleCardContentItemContent$key } from "~/queries/__generated__/magazinesStyleCardContentItemContent.graphql";

import { Container, Cover, Headline, StyledEntityLink, Topic } from "./styles";

export type Props = {
  className?: string;
  provider: DefaultComponentsProvider;
  reference: magazinesStyleCardContentItemContent$key;
};

export const ContentItemMagazinesStyleCard = forwardRef<HTMLDivElement, Props>(
  ({ className, provider, reference }, elementReference) => {
    const article = useFragment(
      graphql`
        fragment magazinesStyleCardContentItemContent on Content {
          topics {
            entityId
            ...topicLinkTopic
          }
          ...entityLink
          ...helpersCheckIsMainTopicContent
        }
      `,
      reference,
    );

    const cover = (
      <StyledEntityLink reference={article}>
        {provider.coverImage({
          responsiveVariants: {
            desktopUp: "size400x400",
            mobileUp: "size400x400",
            tabletUp: "size400x400",
          },
        })}
      </StyledEntityLink>
    );
    const mainTopic = useMemo(() => {
      const topics = sanitizeReadonlyArray(article.topics);
      return topics.find(t => checkIsMainTopic(article, t.entityId));
    }, [article]);

    const headline = (
      <StyledEntityLink reference={article}>
        {provider.headline({ preferSocialHeadline: true })}
      </StyledEntityLink>
    );

    return (
      <Container className={className} ref={elementReference}>
        <Cover>{cover}</Cover>
        {mainTopic ? <Topic reference={mainTopic} /> : null}
        <Headline>{headline}</Headline>
      </Container>
    );
  },
);

ContentItemMagazinesStyleCard.displayName = "ContentItemMagazinesStyleCard";
